import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {UnmountClosed} from 'react-collapse';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {Alert, Button, Checkbox, DatePicker, Form, Select, Space, Tooltip} from 'antd';
import {StyledExportModal, StyledExportModalAccountItem, StyledExportModalBlockedMessage, StyledExportModalCheckboxItem, StyledExportModalInfoIcon} from './StyledExportModal';
import RadioGroupIcon from '../../../RadioGroupIcon';
import SpinSmall from '../../../SpinSmall';
import {FileCsvIcon, FilePdfIcon, FileXlsIcon, FileZipIcon} from '../../../../icons';
import {ReactComponent as ExportSvgImage} from '../../../../static/images/pages/transactions/export-transactions.svg';
import {objectHelpers} from '../../../../utils/helpers';
import RangePicker from '../../../../components/RangePicker';

const {Item} = Form;

const baseDate = dayjs().subtract(1, 'month');
const endOfPreviousMonth = baseDate.endOf('month');

const defaultFormValues = {
  format: 'pdf',
  date: baseDate,
  date_range: []
};

const getFormValues = (checkboxes, defaultAccount) => {
  const base = defaultAccount ? {...defaultFormValues, account: defaultAccount} : defaultFormValues;
  return checkboxes.reduce(
    (acc, checkbox) => ({...acc, [checkbox.name]: Boolean(checkbox.forceChecked)}),
    base,
  );
};

const pickerProps = {
  size: 'middle'
}

const ExportModal = ({
  accountOptions = null,
  defaultAccount,
  enableFormatField = true,
  showDateField = true,
  extraCheckboxes = null,
  error,
  handleCancel,
  handleOk,
  loading = false,
  open = false,
  options = ['csv', 'pdf', 'xls', 'zip'],
  title,
  ...rest
}) => {
  const [form] = Form.useForm();
  const [t] = useTranslation('main');
  const checkboxes = useMemo(() => extraCheckboxes || [], [extraCheckboxes]);
  const [formValues, setFormValues] = useState(() => getFormValues(checkboxes, defaultAccount));

  const format = Form.useWatch('format', form);
  const date = Form.useWatch('date', form);
  const dateRange = Form.useWatch('date_range', form);
  const account = Form.useWatch('account', form);

  const hasAccountSelector = Array.isArray(accountOptions) && accountOptions.length > 0;

  const effectiveFormats = useMemo(() => {
    if (!hasAccountSelector) return options;
    return accountOptions.find((option) => option.value === account)?.formats ?? options;
  }, [accountOptions, account, hasAccountSelector, options]);

  const disabledOkButton = useMemo(() => {
    if (!showDateField) return false;
    if (loading) return true;

    return format === 'pdf'
      ? !objectHelpers.isValidValue(date)
      : !(dateRange && dateRange.length >= 2);
  }, [date, dateRange, format, loading, showDateField]);

  // Each checkbox is shown only when the selected format and account both allow it
  const isCheckboxShown = (checkbox) => {
    const allowedForFormat = !checkbox.formats || checkbox.formats.includes(format);
    const allowedForAccount = !hasAccountSelector || !checkbox.accounts || checkbox.accounts.includes(account);
    return allowedForFormat && allowedForAccount;
  };
  const visibleCheckboxes = useMemo(
    () => checkboxes.filter(isCheckboxShown),
    [checkboxes, format, account, hasAccountSelector], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // A checkbox can be `forceChecked` (e.g. "Include documents" when one card is selected) —
  // keep the form's value in sync so a disabled, forced checkbox still submits `true`, and
  // clears again once `forceChecked` goes back to `false` (e.g. the card filter is cleared)
  // instead of leaving a stale, still-enabled tick behind. Checkboxes that never declare
  // `forceChecked` at all (e.g. "Include pending transactions") are left untouched.
  //
  // Scoped to `visibleCheckboxes`, not every config entry: a caller can give two entries the
  // same `name` with non-overlapping `formats` (one opt-in, one forced) to vary forceChecked
  // by format — e.g. "Include pending transactions" is forced only for a single-card PDF. If
  // this read every entry, the forced one would win even while its non-forced sibling (a
  // different format) is the one actually shown.
  //
  // Depends on a `name:value` signature, not `visibleCheckboxes` itself: callers like
  // TableFilters pass a freshly-built array on every one of their own re-renders (unrelated
  // state, e.g. the search box), and this modal stays mounted via `forceRender` even while
  // closed. Keying off the array would re-run this effect on every such render and force an
  // un-forced checkbox (e.g. "Include documents" for the all-cards, opt-in case) back to
  // `false`, wiping a tick the user just made. The signature only changes when a
  // `forceChecked` value actually flips, or when which entry is visible changes.
  const forceCheckedSignature = visibleCheckboxes
    .map((checkbox) => ('forceChecked' in checkbox ? `${checkbox.name}:${Boolean(checkbox.forceChecked)}` : ''))
    .join('|');
  useEffect(() => {
    visibleCheckboxes.forEach((checkbox) => {
      if (!('forceChecked' in checkbox)) return;
      const value = Boolean(checkbox.forceChecked);
      form.setFieldValue(checkbox.name, value);
      setFormValues((prev) => ({...prev, [checkbox.name]: value}));
    });
  }, [forceCheckedSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  const radioOptions = useMemo(() => {
    return [
      {icon: <FilePdfIcon />, label: 'PDF', value: 'pdf'},
      {icon: <FileCsvIcon />, label: 'CSV', value: 'csv'},
      {icon: <FileXlsIcon />, label: 'Excel', value: 'xls', className: 'excel-icon'},
      {icon: <FileZipIcon />, label: 'ZIP', value: 'zip'}
    ].filter(option => effectiveFormats.includes(option.value));
  }, [effectiveFormats]);

  useEffect(() => {
    if (!open) return;
    // Sync the account selection to the latest default whenever the modal (re)opens.
    // Derive the format list directly from defaultAccount — Form.useWatch('account')
    // is still stale here, so effectiveFormats reflects the previous account.
    const nextFormats = hasAccountSelector && defaultAccount
      ? accountOptions.find((option) => option.value === defaultAccount)?.formats ?? options
      : effectiveFormats;
    if (hasAccountSelector && defaultAccount) {
      setFormValues(prev => ({...prev, account: defaultAccount}));
      form.setFieldValue('account', defaultAccount);
    }
    if (nextFormats.length > 1 && !nextFormats.includes('pdf')) {
      const format = nextFormats[0];
      setFormValues(prev => ({...prev, format}));
      form.setFieldValue('format', format);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the selected account changes the available formats, drop an invalid format selection
  useEffect(() => {
    if (!open || !effectiveFormats.length || effectiveFormats.includes(format)) return;
    const nextFormat = effectiveFormats[0];
    setFormValues(prev => ({...prev, format: nextFormat}));
    form.setFieldValue('format', nextFormat);
  }, [effectiveFormats]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAfterClose = () => {
    const values = getFormValues(checkboxes, defaultAccount);
    setFormValues(values);
    form.setFieldsValue(values);
  }

  const onValuesChange = (values) => {
    setFormValues(prev => ({...prev, ...values}));
  }

  const footer = [
    <Button
      key='ok'
      disabled={disabledOkButton}
      loading={loading}
      onClick={() => (!disabledOkButton && handleOk) && handleOk(formValues)}
      type='primary'
      size='large'
    >
      {t('export')}
    </Button>
  ];

  return (
    <StyledExportModal
      afterClose={handleAfterClose}
      forceRender
      footer={footer}
      onCancel={handleCancel}
      open={open}
      title={title || `${t('export')}`}
      width={488}
      {...rest}
    >
      <SpinSmall spinning={loading}>
        <Space direction='vertical' size='middle'>
          <ExportSvgImage />
          <Form
            hideRequiredMark
            form={form}
            initialValues={formValues}
            layout='vertical'
            onValuesChange={onValuesChange}
          >
            {hasAccountSelector && (
              <StyledExportModalAccountItem
                label={t('account')}
                name='account'
                required
              >
                <Select options={accountOptions} size='middle' />
              </StyledExportModalAccountItem>
            )}
            {showDateField && (format === 'pdf' ? (
              <Item
                label={t('selectDate')}
                name='date'
              >
                <DatePicker
                  {...pickerProps}
                  disabledDate={(current) => current.isAfter(endOfPreviousMonth)}
                  format='MMM YYYY'
                  picker='month'
                  placeholder={t('selectDate')}
                />
              </Item>
            ) : (
              <Item
                label={t('selectDate')}
                name='date_range'
              >
                <RangePicker
                  {...pickerProps}
                  disabledDate={(current) => current.isAfter(dayjs())}
                />
              </Item>
            ))}
            {enableFormatField && (
              <Item
                label={`${t('select')} ${t('format')}`}
                name='format'
              >
                <RadioGroupIcon options={radioOptions} />
              </Item>
            )}
            {visibleCheckboxes.map((checkbox) => (
              <StyledExportModalCheckboxItem key={checkbox.name} name={checkbox.name} valuePropName='checked'>
                <Checkbox disabled={checkbox.disabled}>
                  {checkbox.label}
                  {checkbox.tooltip && (
                    <Tooltip title={checkbox.tooltip}>
                      <StyledExportModalInfoIcon />
                    </Tooltip>
                  )}
                </Checkbox>
              </StyledExportModalCheckboxItem>
            ))}
          </Form>
        </Space>
        <UnmountClosed isOpened={Boolean(error)}>
          <StyledExportModalBlockedMessage>
            <Alert message={error} type='error' showIcon />
          </StyledExportModalBlockedMessage>
        </UnmountClosed>
      </SpinSmall>
    </StyledExportModal>
  );
}

ExportModal.propTypes = {
  accountOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    formats: PropTypes.arrayOf(PropTypes.string).isRequired,
  })),
  defaultAccount: PropTypes.string,
  enableFormatField: PropTypes.bool,
  showDateField: PropTypes.bool,
  extraCheckboxes: PropTypes.arrayOf(PropTypes.shape({
    accounts: PropTypes.arrayOf(PropTypes.string),
    disabled: PropTypes.bool,
    forceChecked: PropTypes.bool,
    formats: PropTypes.arrayOf(PropTypes.string),
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    tooltip: PropTypes.string
  })),
  error: PropTypes.string,
  handleCancel: PropTypes.func,
  handleOk: PropTypes.func,
  loading: PropTypes.bool,
  open: PropTypes.bool,
  options: PropTypes.array
}

export default ExportModal;


